import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Redirect } from 'react-router-dom/cjs/react-router-dom.min';
import { useTranslation } from 'react-i18next';

import { translateError } from '../utils/translateError';
import DynamicEventServiceDetailsForm from '../components/event-details/dynamic-event-service-details';
import {
  getEvent,
  getEventSlots,
  getServiceSlot,
  startLoading,
  submitEventBooking,
  submitEventWaitlist,
} from '../state/actions/service';
import '../styles/service-description.scss';

function DynamicEventDetails() {
  const { t, i18n } = useTranslation();
  // 1. Initialize the state to use localStorage if available, otherwise i18n.resolvedLanguage.
  const [resolvedLanguage, setLanguage] = useState(
    localStorage.getItem('language') || i18n.resolvedLanguage
  );
  // 2. When the language state changes, update i18next (using the normalized resolvedLanguage),
  //    persist it to localStorage, and log the normalized language.
  useEffect(() => {
    i18n.changeLanguage(resolvedLanguage);
    localStorage.setItem('language', resolvedLanguage);
  }, [resolvedLanguage, i18n]);

  // Subscribe to i18next language changes and update resolvedLanguage state accordingly.
  useEffect(() => {
    const handleLanguageChange = () => {
      // Update the state to the normalized language (resolvedLanguage)
      setLanguage(i18n.resolvedLanguage);
    };
    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const history = useHistory();
  const dispatch = useDispatch();
  const service = useSelector((state) => state.service);
  const { slug } = useParams();

  const onFormSubmitHandler = (data) => {
    dispatch(startLoading());
    if (data.status_id === 5) {
      dispatch(submitEventWaitlist({ ...data, event_id: parseInt(service.event.id, 10) }, history));
    } else {
      dispatch(submitEventBooking({ ...data, event_id: parseInt(service.event.id, 10) }, history));
    }
  };

  useEffect(() => {
    dispatch(getEvent(slug));
    dispatch(getEventSlots(slug));
  }, [slug, dispatch]);

  const serviceSlotId = service && service.event && service.event.serviceSlotId;

  useEffect(() => {
    if (serviceSlotId) {
      console.log('service.event.service_slot_id	', serviceSlotId);
      dispatch(getServiceSlot(serviceSlotId));
    }
  }, [dispatch, serviceSlotId]);

  if (!service.event && !service.loading && service.error === 'event_does_not_exist') {
    return <Redirect to="/" />;
  }
  const err = service.error || service.slotsError || service.serviceSlotError;

  return (
    <div className="container">
      <div className="language-switch">
        <button
          className={`language-option ${resolvedLanguage === 'sv' ? 'active' : ''}`}
          onClick={() => setLanguage('sv')}
        >
          Svenska
        </button>
        <button
          className={`language-option ${resolvedLanguage === 'en' ? 'active' : ''}`}
          onClick={() => setLanguage('en')}
        >
          English
        </button>
      </div>
      <ServiceDescription event={service.event} eventSlotsYear={service.eventSlotsYear} />
      <DynamicEventServiceDetailsForm
        onSubmit={onFormSubmitHandler}
        slots={service.slots}
        isLoading={service.loading}
        errorMessage={err ? translateError(err) : null}
        /*errorMessage={err ? translateError(err.event ? err.message : err) : null}*/
        eventStatus={service.event ? service.event.eventStatusId : null}
        serviceSlot={service.serviceSlot}
      />

      <div className="privacy-policy">
        <div>
          {t('privacy_policy_accept', 'I hereby accept MIOO Cycling’s')}
          <br />
          <div className="text-center">
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://mioo.cc/sv/om-mioo/integritetspolicy"
            >
              {t('privacy_policy_link', 'Privacy Policy.')}
            </a>
          </div>
        </div>
      </div>
      {service.event && service.event.logo && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            paddingTop: 20,
            paddingBottom: 20,
            flexDirection: 'column',
          }}
        >
          <h5 style={{ fontWeight: 'bold' }}>MIOO</h5>
          <p style={{ fontStyle: 'italic' }}>{t('in_collaboration', 'In collaboration with')}</p>
          <img className="logo" src={service.event.logo} alt="company-logo" width="100" />
        </div>
      )}
    </div>
  );
}

function ServiceDescription(props) {
  const { t, i18n } = useTranslation();
  const event = props.event;
  // Determine display values based on the current language:
  let displayName = event ? event.name : '';
  let displayEventDescription = event ? event.eventDescription : '';
  let displayPlace = event ? event.place : '';
  let displaySeason = event ? event.season : '';
  const eventSlotsYear = props.eventSlotsYear;
  if (event) {
    if (i18n.resolvedLanguage && i18n.resolvedLanguage.startsWith('en')) {
      displayName = event.rawNameEng || displayName;
      displayEventDescription = event.rawEventDescriptionEng || displayEventDescription;
      displayPlace = event.rawPlaceEng || displayPlace;
      displaySeason = event.rawSeasonEng || displaySeason;
    } else {
      displayName = event.rawName || displayName;
      displayEventDescription = event.rawEventDescription || displayEventDescription;
      displayPlace = event.rawPlace || displayPlace;
      displaySeason = event.rawSeason || displaySeason;
    }
  }
  const displaySeasonWithYear =
    displaySeason && eventSlotsYear ? `${displaySeason} ${eventSlotsYear}` : displaySeason;
  return (
    <div className="service-description">
      <h1>{t('bike_service', 'Bike service')}</h1>
      <div className="break-line" />

      {props.event && (
        <div>
          <div className="event-header">
            {event.companyName ? <h4>{event.companyName}</h4> : null}
            {displayName ? <h4 className="event-name">{displayName}</h4> : null}
            {displaySeasonWithYear ? <h5>{displaySeasonWithYear}</h5> : null}
          </div>
          <p>{displayEventDescription}</p>
          <p>
            {t(
              'drop_off_instruction',
              'You drop off your bike at your booked time and will be informed on-site when it is expected to be ready.'
            )}
          </p>
          <p>
            <b>{t('included_service', 'Included in the service:')}</b>
          </p>
          <ul style={{ listStyle: 'circle', marginLeft: '14px' }}>
            <li>{t('adjustment', 'Adjustment of gears, brakes, and tire pressure')}</li>
            <li>{t('tightening', 'Tightening of bolts and nuts')}</li>
            <li>{t('cleaning', 'Cleaning and chain lubrication (no full wash)')}</li>
            <li>{t('service_protocol', 'Service protocol')}</li>
          </ul>
          <p>
            {t(
              'replacement_info',
              'At some of our events, we offer the option to replace worn spare parts. If this is included, you will receive information upon check-in of your bike.'
            )}
          </p>
          <p>
            {t(
              'replacement_disclaimer',
              'Any parts that need replacing will be done upon agreement and charged separately on-site.'
            )}
          </p>
          <p>
            <b>{t('location', 'Location:')}</b> {props.event.location}
          </p>
          <p>
            <b>{t('place', 'Place:')}</b> {displayPlace}
          </p>
        </div>
      )}
    </div>
  );
}
export default DynamicEventDetails;
